/**
 * Parse XML hóa đơn điện tử VN (NĐ 123 / TT 78 — schema GDT).
 * Map header + DSHHDVu → form ghi nhận HĐ.
 */

import { calcItemAmount } from "./invoiceItemsImport";

const textOf = (el, tag) => {
  if (!el) return "";
  const node =
    el.getElementsByTagName(tag)?.[0] ||
    el.querySelector?.(tag) ||
    null;
  return String(node?.textContent || "").trim();
};

const firstByLocal = (root, localName) => {
  if (!root) return null;
  const all = root.getElementsByTagName("*");
  for (let i = 0; i < all.length; i += 1) {
    const n = all[i];
    const local = (n.localName || n.nodeName || "").replace(/^.*:/, "");
    if (local.toLowerCase() === localName.toLowerCase()) return n;
  }
  return null;
};

const allByLocal = (root, localName) => {
  if (!root) return [];
  const out = [];
  const all = root.getElementsByTagName("*");
  for (let i = 0; i < all.length; i += 1) {
    const n = all[i];
    const local = (n.localName || n.nodeName || "").replace(/^.*:/, "");
    if (local.toLowerCase() === localName.toLowerCase()) out.push(n);
  }
  return out;
};

const childText = (parent, localName) => {
  if (!parent) return "";
  const kids = parent.children || parent.childNodes || [];
  for (let i = 0; i < kids.length; i += 1) {
    const n = kids[i];
    if (n.nodeType !== 1) continue;
    const local = (n.localName || n.nodeName || "").replace(/^.*:/, "");
    if (local.toLowerCase() === localName.toLowerCase()) {
      return String(n.textContent || "").trim();
    }
  }
  // fallback deep search within parent only
  const found = allByLocal(parent, localName).find((n) => n.parentNode === parent);
  return found ? String(found.textContent || "").trim() : textOf(parent, localName);
};

const parseNumberVN = (raw) => {
  if (raw == null || raw === "") return NaN;
  if (typeof raw === "number") return raw;
  let s = String(raw).trim().replace(/\s/g, "");
  if (/\d,\d{1,4}$/.test(s) && s.includes(".")) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else if (/\d\.\d{1,4}$/.test(s) && s.includes(",")) {
    s = s.replace(/,/g, "");
  } else {
    s = s.replace(/,/g, "");
  }
  s = s.replace(/[^\d.-]/g, "");
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

/** "10%", "10", "8.0", "KCT", "KKKNT" → percent number or null */
const parseTaxRate = (raw) => {
  const s = String(raw || "").trim().toUpperCase();
  if (!s || s === "KCT" || s === "KKKNT" || s === "KHAC" || s === "-1") {
    return s === "KCT" || s === "KKKNT" ? 0 : null;
  }
  const m = s.match(/(\d+(?:[.,]\d+)?)/);
  if (!m) return null;
  return parseNumberVN(m[1]);
};

const parseInvoiceDate = (raw) => {
  const s = String(raw || "").trim();
  if (!s) return "";
  // YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  // DD/MM/YYYY or DD-MM-YYYY
  const dmy = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})/);
  if (dmy) {
    return `${dmy[3]}-${dmy[2].padStart(2, "0")}-${dmy[1].padStart(2, "0")}`;
  }
  return "";
};

const buildInvoiceNumber = (ttChung) => {
  const khms = childText(ttChung, "KHMSHDon");
  const kh = childText(ttChung, "KHHDon");
  const so = childText(ttChung, "SHDon");
  if (!so && !kh && !khms) return "";
  if (kh && so) return `${kh}-${so}`;
  if (khms && so) return `${khms}-${so}`;
  return so || kh || khms;
};

/**
 * @param {string} xmlText
 * @param {{ invoiceType?: 'purchase'|'sale' }} opts
 * @returns {{ header: object, items: array, warnings: string[] }}
 */
export function parseInvoiceXmlString(xmlText, opts = {}) {
  const invoiceType = opts.invoiceType === "sale" ? "sale" : "purchase";
  const warnings = [];

  let text = String(xmlText || "").replace(/^\uFEFF/, "");
  if (!text.trim()) {
    throw new Error("File XML trống.");
  }

  // Một số NCC bọc XML trong chuỗi escaped
  if (!text.includes("<") && text.includes("&lt;")) {
    const ta = document.createElement("textarea");
    ta.innerHTML = text;
    text = ta.value;
  }

  const doc = new DOMParser().parseFromString(text, "text/xml");
  const parseErr = doc.querySelector("parsererror");
  if (parseErr) {
    throw new Error("XML không hợp lệ hoặc không phải hóa đơn điện tử.");
  }

  const hdon = firstByLocal(doc, "HDon") || doc.documentElement;
  const dl = firstByLocal(hdon, "DLHDon") || hdon;
  const ttChung = firstByLocal(dl, "TTChung");
  const nd = firstByLocal(dl, "NDHDon") || dl;

  if (!ttChung && !firstByLocal(nd, "DSHHDVu") && !allByLocal(nd, "HHDVu").length) {
    throw new Error(
      "Không nhận diện được XML HĐĐT (thiếu TTChung / DSHHDVu). Kiểm tra file từ phần mềm hóa đơn."
    );
  }

  const nBan = firstByLocal(nd, "NBan");
  const nMua = firstByLocal(nd, "NMua");
  const partnerEl = invoiceType === "sale" ? nMua : nBan;
  const partnerLabel = invoiceType === "sale" ? "người mua" : "người bán";

  if (!partnerEl) {
    warnings.push(`Không tìm thấy thông tin ${partnerLabel} trên XML.`);
  }

  const invoice_number = buildInvoiceNumber(ttChung);
  const invoice_date = parseInvoiceDate(childText(ttChung, "NLap"));

  const partner_name =
    childText(partnerEl, "Ten") ||
    childText(partnerEl, "HVTNMHang") ||
    "";
  const partner_tax_code = childText(partnerEl, "MST") || "";

  const ttoan = firstByLocal(nd, "TToan");
  const tgTCThue = parseNumberVN(childText(ttoan, "TgTCThue"));
  const tgTThue = parseNumberVN(childText(ttoan, "TgTThue"));
  const tgTTTBSo = parseNumberVN(childText(ttoan, "TgTTTBSo"));

  let vat_percent = "";
  if (tgTCThue > 0 && tgTThue >= 0) {
    vat_percent = String(Math.round((tgTThue / tgTCThue) * 10000) / 100);
  }

  const hhdvuNodes = allByLocal(nd, "HHDVu");
  const items = [];
  const rates = [];

  hhdvuNodes.forEach((node, idx) => {
    const tchat = childText(node, "TChat");
    // TChat=4 thường là ghi chú / diễn giải không phải hàng hóa
    if (tchat === "4") return;

    const item_name =
      childText(node, "THHDVu") || childText(node, "Ten") || "";
    if (!item_name) return;

    const unit = childText(node, "DVTinh") || "";
    let quantity = parseNumberVN(childText(node, "SLuong"));
    let unit_price = parseNumberVN(childText(node, "DGia"));
    let amount = parseNumberVN(childText(node, "ThTien"));

    if (!(quantity > 0)) quantity = 1;
    if (Number.isNaN(unit_price) || unit_price < 0) {
      unit_price = amount > 0 && quantity > 0 ? amount / quantity : 0;
    }
    if (Number.isNaN(amount) || amount < 0) {
      amount = calcItemAmount(quantity, unit_price);
    } else {
      amount = Math.round(amount);
    }

    const rate = parseTaxRate(childText(node, "TSuat"));
    if (rate != null) rates.push(rate);

    // Không gắn STT vào tên/note — số dòng hiển thị riêng trên UI
    const note = childText(node, "LHHDVu") || "";
    const cleanName = String(item_name)
      .replace(/\s*STT\s*\d+\s*$/i, "")
      .trim();

    items.push({
      key: `xml-${idx}-${Date.now()}-${items.length}`,
      item_name: cleanName || item_name,
      unit,
      quantity: String(quantity),
      unit_price: String(Math.round(unit_price)),
      amount: String(amount),
      note,
    });
  });

  if (!vat_percent && rates.length) {
    const uniq = [...new Set(rates.filter((r) => r != null))];
    if (uniq.length === 1) vat_percent = String(uniq[0]);
    else if (uniq.length > 1) {
      warnings.push(
        `XML có nhiều thuế suất (${uniq.join("%, ")}%). Đã lấy tỷ lệ từ tổng thanh toán nếu có.`
      );
      if (!(tgTCThue > 0)) vat_percent = String(uniq[0]);
    }
  }

  if (!items.length) {
    throw new Error("XML không có dòng hàng hóa / dịch vụ (HHDVu) hợp lệ.");
  }

  const subtotalFromItems = items.reduce(
    (acc, row) => acc + (Number(row.amount) || 0),
    0
  );
  const subtotal =
    tgTCThue > 0 ? Math.round(tgTCThue) : Math.round(subtotalFromItems);
  const total_amount =
    tgTTTBSo > 0
      ? Math.round(tgTTTBSo)
      : Math.round(
          subtotal +
            (tgTThue >= 0
              ? tgTThue
              : subtotal * ((Number(vat_percent) || 0) / 100))
        );

  const seriesHint = [
    childText(ttChung, "KHMSHDon") && `Mẫu ${childText(ttChung, "KHMSHDon")}`,
    childText(ttChung, "KHHDon") && `KH ${childText(ttChung, "KHHDon")}`,
  ]
    .filter(Boolean)
    .join(" · ");

  const header = {
    invoice_number: invoice_number || "",
    invoice_date: invoice_date || "",
    partner_name,
    partner_tax_code,
    partner_customer_id: "",
    subtotal: subtotal > 0 ? String(subtotal) : "",
    vat_percent: vat_percent !== "" && vat_percent != null ? String(vat_percent) : "",
    total_amount: total_amount > 0 ? String(total_amount) : "",
    description: seriesHint || "",
  };

  if (tgTCThue > 0 && Math.abs(tgTCThue - subtotalFromItems) > 1) {
    warnings.push(
      `Tổng dòng (${subtotalFromItems.toLocaleString("vi-VN")}) lệch TgTCThue XML (${Math.round(tgTCThue).toLocaleString("vi-VN")}). Giữ tổng theo XML.`
    );
  }

  return { header, items, warnings };
}

/**
 * @param {File} file
 * @param {{ invoiceType?: 'purchase'|'sale' }} opts
 */
export async function parseInvoiceXmlFile(file, opts = {}) {
  const name = String(file?.name || "").toLowerCase();
  if (name && !name.endsWith(".xml")) {
    // vẫn thử parse nếu user chọn nhầm extension nhưng nội dung XML
  }
  const buffer = await file.arrayBuffer();
  let text = new TextDecoder("utf-8").decode(buffer);
  // UTF-16 LE BOM
  if (buffer.byteLength >= 2) {
    const u8 = new Uint8Array(buffer);
    if (u8[0] === 0xff && u8[1] === 0xfe) {
      text = new TextDecoder("utf-16le").decode(buffer);
    } else if (u8[0] === 0xfe && u8[1] === 0xff) {
      text = new TextDecoder("utf-16be").decode(buffer);
    }
  }
  return parseInvoiceXmlString(text, opts);
}
